import React, { useRef, useState, useEffect } from 'react';
import { App, Button, Typography, theme } from 'antd';
import { PlusOutlined, CloseOutlined } from '@ant-design/icons';

interface ScreenshotUploaderProps {
  onFileSelect: (file: File | null) => void;
  selectedFile: File | null;
  disabled?: boolean;
}

/**
 * File picker for screenshot uploads with preview
 */
export function ScreenshotUploader({
  onFileSelect,
  selectedFile,
  disabled = false,
}: ScreenshotUploaderProps) {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef(previewUrl);

  useEffect(() => {
    previewUrlRef.current = previewUrl;
  }, [previewUrl]);

  useEffect(() => {
    if (!selectedFile && previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      setPreviewUrl(null);
    }
  }, [selectedFile]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      message.error('Please upload a valid image file (PNG, JPG, GIF, or WebP)');
      return;
    }

    // 1 MB metric (1,000,000 bytes) — leaves ~48 KB of headroom under
    // the nginx 1 MiB `proxy-body-size` cap for multipart overhead.
    const maxSize = 1000 * 1000;
    if (file.size > maxSize) {
      message.error('File size must be less than 1MB');
      return;
    }

    // Revoke old URL before creating new one to prevent memory leak
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    onFileSelect(file);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    // Revoke URL before clearing to prevent memory leak
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    setPreviewUrl(null);
    onFileSelect(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {!selectedFile ? (
        <Button
          type="text"
          icon={<PlusOutlined />}
          onClick={handleButtonClick}
          disabled={disabled}
          style={{
            color: token.colorTextSecondary,
            padding: `0 ${token.paddingXS}px`,
          }}
        >
          Attach image
        </Button>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: token.sizeXS,
            padding: token.paddingXS,
            backgroundColor: token.colorBgContainer,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadius,
          }}
        >
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Preview"
              style={{
                width: 40,
                height: 40,
                objectFit: 'cover',
                borderRadius: token.borderRadiusSM,
              }}
            />
          )}
          <Typography.Text
            style={{
              flex: 1,
              fontSize: token.fontSizeSM,
              color: token.colorTextSecondary,
            }}
            ellipsis
          >
            {selectedFile.name}
          </Typography.Text>
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={handleRemoveFile}
            disabled={disabled}
            style={{ color: token.colorTextSecondary }}
          />
        </div>
      )}
    </div>
  );
}
